import type { World } from '../core/world';
import type { Renderer } from '../platform/render/renderer';
import type { AudioPlayer } from '../platform/audio/audioPlayer';
import type { Services } from '../services';
import { KeyboardInput } from '../platform/input/keyboardInput';
import { PointerInput } from '../platform/input/pointerInput';
import type { Intent } from '../platform/input/intent';
import { GameUI } from '../ui/gameUi';
import { SCORING, JUICE, PACE_PRESETS, DEFAULT_PACE } from '../core/config/gameConfig';
import { GAME_MODES } from '../core/config/modes';
import { digitPlaybackRate } from '../core/config/audioManifest';
import { vibrate } from '../platform/haptics';
import { readJson, writeJson } from '../services/local/storage';

export interface GameDeps {
  world: World;
  renderer: Renderer;
  audio: AudioPlayer;
  services: Services;
}

interface Settings {
  modeId: string;
  paceId: string;
  reflections: boolean;
  bloom: boolean;
  debrisPhysics: boolean;
  reduceMotion: boolean;
}

const SETTINGS_KEY = 'cs.settings';

// Touch devices default to the cheaper profile; desktops get the spectacle.
const IS_TOUCH = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

// The application controller. Owns the frame loop and is the only place that
// knows about every layer at once: it maps input intents to World commands,
// World events to audio/persistence, and steps render each frame. Everything it
// touches is an interface, so any piece can be swapped for tests or platforms.
export class Game {
  private rafId = 0;
  private lastTime = 0;
  private settings: Settings;
  private best = 0;
  /** Hit-stop: sim time freezes for a beat on every kill (render keeps going). */
  private freezeUntil = 0;
  /** Slow-mo flourish window after level/stage changes. */
  private slowMoUntil = 0;
  private ui!: GameUI;
  private readonly keyboard = new KeyboardInput();
  private pointer: PointerInput | null = null;

  constructor(private readonly deps: GameDeps) {
    this.settings = readJson<Settings>(SETTINGS_KEY, {
      modeId: deps.world.currentMode.id,
      paceId: DEFAULT_PACE,
      reflections: true,
      bloom: !IS_TOUCH,
      debrisPhysics: !IS_TOUCH,
      reduceMotion: false,
    });
  }

  async start(root: HTMLElement): Promise<void> {
    const { world, renderer, services } = this.deps;
    await renderer.init(root, world.events);

    // Restore last session's mode/pace/settings before building the UI.
    if (GAME_MODES[this.settings.modeId]) world.setMode(GAME_MODES[this.settings.modeId]);
    if (PACE_PRESETS[this.settings.paceId]) world.setPace(this.settings.paceId);
    renderer.setEnvironmentEnabled(this.settings.reflections);
    renderer.setQuality({
      bloom: this.settings.bloom,
      debrisPhysics: this.settings.debrisPhysics,
      motion: !this.settings.reduceMotion,
    });

    this.ui = new GameUI(root, {
      title: 'CountStrike',
      modes: Object.values(GAME_MODES),
      selectedModeId: world.currentMode.id,
      paces: Object.entries(PACE_PRESETS).map(([id, p]) => ({ id, label: p.label })),
      selectedPaceId: world.currentPace,
      actions: {
        start: () => this.beginRun(),
        restart: () => this.beginRun(),
        quit: () => world.reset(),
        resume: () => world.setPaused(false),
        togglePause: () => world.togglePause(),
        changeWeapon: (delta) => this.dispatch({ type: 'changeWeapon', delta }),
        toggleReflections: (enabled) => {
          this.settings.reflections = enabled;
          this.saveSettings();
          renderer.setEnvironmentEnabled(enabled);
        },
        toggleQuality: (key, enabled) => {
          this.settings[key] = enabled;
          this.saveSettings();
          if (key === 'reduceMotion') renderer.setQuality({ motion: !enabled });
          else renderer.setQuality({ [key]: enabled });
        },
        selectMode: (id) => this.selectMode(id),
        setPace: (id) => {
          this.settings.paceId = id;
          this.saveSettings();
          world.setPace(id);
        },
      },
    });
    this.ui.bind(world.events);
    this.ui.setReflections(this.settings.reflections);
    this.ui.setQualityChecks(this.settings);
    this.refreshModeInfo();
    this.wireAudio();
    this.wireTeaching();
    this.wireJuice();
    this.wirePersistence();

    this.best = await services.scores.getBest(world.currentMode.id);
    this.ui.setBest(this.best);
    void this.refreshModeBests();

    this.keyboard.attach((i) => this.dispatch(i));
    if (renderer.canvas) {
      this.pointer = new PointerInput(renderer.canvas);
      this.pointer.attach((i) => this.dispatch(i));
    }
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);

    // Reflect the initial (idle) status so the home screen shows.
    world.events.emit('statusChanged', { status: world.state.status });
    this.ui.setScore(world.state.score);

    this.lastTime = performance.now();
    this.loop(this.lastTime);
  }

  private selectMode(id: string): void {
    const mode = GAME_MODES[id];
    if (!mode) return;
    this.settings.modeId = id;
    this.saveSettings();
    this.deps.world.setMode(mode);
    this.refreshModeInfo();
    void this.deps.services.scores.getBest(id).then((best) => {
      this.best = best;
      this.ui.setBest(best);
    });
  }

  private refreshModeInfo(): void {
    const { world } = this.deps;
    this.ui.setModeInfo(
      world.currentMode.label,
      `${world.currentRule.instructions} ` +
        `Hit +${SCORING.hit}, shot ${SCORING.shot}, weapon change ${SCORING.weaponChange}. ` +
        `At ${SCORING.floor} you can't shoot — and it gets faster.`,
    );
  }

  private async refreshModeBests(): Promise<void> {
    for (const mode of Object.values(GAME_MODES)) {
      const best = await this.deps.services.scores.getBest(mode.id);
      this.ui.setModeBest(mode.id, best);
    }
  }

  private saveSettings(): void {
    writeJson(SETTINGS_KEY, this.settings);
  }

  private beginRun(): void {
    this.deps.audio.unlock();
    this.deps.world.start();
  }

  private dispatch(intent: Intent): void {
    const { world, audio, renderer } = this.deps;
    audio.unlock();
    switch (intent.type) {
      case 'shoot':
        world.shoot();
        break;
      case 'changeWeapon':
        world.changeWeapon(intent.delta);
        audio.play(intent.delta > 0 ? 'increase' : 'decrease');
        break;
      case 'setWeapon': {
        const prev = world.state.weapon;
        world.setWeapon(intent.weapon);
        if (world.state.weapon !== prev) audio.play('increase');
        break;
      }
      case 'togglePause':
        world.togglePause();
        break;
      case 'toggleReflections':
        this.settings.reflections = !this.settings.reflections;
        this.saveSettings();
        renderer.setEnvironmentEnabled(this.settings.reflections);
        this.ui.setReflections(this.settings.reflections);
        break;
    }
  }

  private wireAudio(): void {
    const { world, audio } = this.deps;
    let lastMultiplier = 1;
    world.events.on('projectileFired', () => audio.play('shoot'));
    world.events.on('enemyDestroyed', ({ enemy }) => {
      // Musical destruction: each digit plays its own pentatonic note, so
      // kill streaks literally form melodies.
      audio.play('hit', { rate: digitPlaybackRate(enemy.value) });
      audio.play('explode', { volume: 0.6, rate: 0.9 + Math.random() * 0.2 });
      vibrate(15);
    });
    world.events.on('comboChanged', ({ combo, multiplier }) => {
      if (multiplier > lastMultiplier) audio.play('combo', { rate: 1 + multiplier * 0.1 });
      else if (combo === 0 && lastMultiplier > 1) audio.play('comboLost');
      lastMultiplier = multiplier;
    });
    world.events.on('answerWrong', () => {
      audio.play('miss');
      vibrate(40);
    });
    world.events.on('powerUpCollected', () => {
      audio.play('powerup');
      vibrate(25);
    });
    world.events.on('gameOver', () => {
      audio.play('explode');
      vibrate([60, 50, 120]);
    });
    world.events.on('levelChanged', () => audio.play('levelup'));
    world.events.on('stageChanged', () => audio.play('levelup', { rate: 0.8, volume: 1 }));
  }

  /** Hit-feel: hit-stop, slow-mo on level-up, kill-spot score pops, power-up
   *  toasts. All app-layer time tricks — the core sim just receives fewer
   *  (or smaller) update ticks and stays deterministic. */
  private wireJuice(): void {
    const { world, renderer } = this.deps;
    let lastKillPos: { x: number; y: number; z: number } | null = null;
    world.events.on('enemyDestroyed', ({ enemy }) => {
      lastKillPos = { ...enemy.position };
      this.freezeUntil = performance.now() + JUICE.hitStopSec * 1000;
    });
    world.events.on('scoreChanged', ({ delta }) => {
      if (delta < 100) return;
      const at = lastKillPos ? renderer.projectToScreen(lastKillPos) : null;
      if (at) this.ui.popScoreAt(`+${delta}`, at.x, at.y);
    });
    const slow = (): void => {
      this.slowMoUntil = performance.now() + JUICE.slowMoSec * 1000;
    };
    world.events.on('levelChanged', slow);
    world.events.on('stageChanged', slow);
    world.events.on('powerUpCollected', ({ kind }) => {
      const labels = { timeFreeze: '❄ Time freeze!', multiShot: '☆ Triple shot!', hint: '✨ Hint glow!' };
      this.ui.showToast(labels[kind]);
    });
  }

  /** Wrong answers become micro-lessons: show what would have worked. */
  private wireTeaching(): void {
    const { world } = this.deps;
    world.events.on('answerWrong', ({ enemy, projectile }) => {
      const answer = world.currentRule.answerFor(enemy.value);
      const lesson =
        enemy.display === String(enemy.value)
          ? `${enemy.display} needs ${answer} — you fired ${projectile.weapon}`
          : `${enemy.display} = ${answer} — you fired ${projectile.weapon}`;
      this.ui.showLesson(lesson);
    });
  }

  private wirePersistence(): void {
    const { world } = this.deps;
    world.events.on('gameOver', ({ score }) => {
      this.ui.showGameOver(score, this.best);
      void this.recordRun(score);
    });
  }

  private async recordRun(score: number): Promise<void> {
    const { world, services } = this.deps;
    const mode = world.currentMode;
    const player = await services.auth.signIn();
    const at = Date.now();
    await services.scores.record({ score, mode: mode.id, durationSec: world.state.elapsed, at });
    await services.leaderboard.submit({ playerId: player.id, name: player.name, score, mode: mode.id, at });
    this.best = await services.scores.getBest(mode.id);
    this.ui.setBest(this.best);
    this.ui.setModeBest(mode.id, this.best);
    this.ui.showGameOver(score, this.best);
  }

  private readonly onResize = (): void => this.deps.renderer.resize();

  /** Auto-pause when the tab hides — the run shouldn't end while you answer
   *  a text message. (Resume is manual, so re-entry is never a surprise.) */
  private readonly onVisibility = (): void => {
    if (document.hidden && this.deps.world.state.status === 'playing') {
      this.deps.world.setPaused(true);
    }
  };

  private readonly loop = (now: number): void => {
    this.rafId = requestAnimationFrame(this.loop);
    const dt = Math.min((now - this.lastTime) / 1000, 0.05);
    this.lastTime = now;
    const { world, renderer } = this.deps;

    // Time tricks: hit-stop skips sim ticks entirely; slow-mo eases the sim
    // back to full speed. Visual FX (shatter, dust) always run at real dt.
    let worldDt = dt;
    if (now < this.freezeUntil) {
      worldDt = 0;
    } else if (now < this.slowMoUntil) {
      const t = 1 - (this.slowMoUntil - now) / (JUICE.slowMoSec * 1000);
      worldDt = dt * (JUICE.slowMoScale + (1 - JUICE.slowMoScale) * t);
    }
    if (worldDt > 0) world.update(worldDt);
    renderer.sync(world.state, dt);
    renderer.render();
  };

  dispose(): void {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.keyboard.detach();
    this.pointer?.detach();
    this.ui.dispose();
    this.deps.renderer.dispose();
  }
}
